/**
 * =============================================================================
 * SPEQ55 RFP Pro - Technical Spec Selector Component
 * =============================================================================
 *
 * Component for selecting and managing technical specifications for RFP analysis.
 *
 * @module components/analysis/SpecSelector
 * @author SPEQ55
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

function SpecSelector({ specs, onChange, availableSpecs }) {
  const [newSpec, setNewSpec] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Determine which specs are currently selected
  const selectedSet = new Set(specs);

  // Split into selected and available
  const selected = specs;
  const available = availableSpecs.filter((s) => !selectedSet.has(s));

  // Show limited items unless showAll is true
  const displayLimit = 15;
  const displayedAvailable = showAll ? available : available.slice(0, displayLimit);

  /**
   * Add a spec to selection.
   */
  const handleAdd = (spec) => {
    if (!selectedSet.has(spec)) {
      onChange([...specs, spec]);
    }
  };

  /**
   * Remove a spec from selection.
   */
  const handleRemove = (spec) => {
    onChange(specs.filter((s) => s !== spec));
  };

  /**
   * Add a custom spec.
   */
  const handleAddCustom = (e) => {
    if (e) e.preventDefault();
    const trimmed = newSpec.trim();
    if (trimmed && !selectedSet.has(trimmed)) {
      onChange([...specs, trimmed]);
      setNewSpec('');
    }
  };

  /**
   * Handle Enter key in input.
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  /**
   * Select all available specs.
   */
  const handleSelectAll = () => {
    onChange([...new Set([...specs, ...availableSpecs])]);
  };

  /**
   * Clear all selections.
   */
  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-accent-700">
          Technical Specifications ({selected.length} selected)
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            Select All
          </button>
          <span className="text-accent-300">|</span>
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs text-accent-500 hover:text-accent-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Selected specs */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((spec) => (
            <span
              key={spec}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary-100 text-secondary-700 text-sm rounded-md"
            >
              {spec}
              <button
                type="button"
                onClick={() => handleRemove(spec)}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add custom spec */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newSpec}
          onChange={(e) => setNewSpec(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add custom specification..."
          className="flex-1 px-3 py-2 text-sm border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={handleAddCustom}
          disabled={!newSpec.trim()}
          className="px-4 py-2 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>

      {/* Available specs */}
      {available.length > 0 && (
        <div>
          <p className="text-xs text-accent-500 mb-2">Click to add:</p>
          <div className="flex flex-wrap gap-2">
            {displayedAvailable.map((spec) => (
              <button
                key={spec}
                type="button"
                onClick={() => handleAdd(spec)}
                className="px-2 py-1 text-sm bg-accent-100 text-accent-600 rounded-md hover:bg-accent-200 transition-colors"
              >
                + {spec}
              </button>
            ))}
          </div>
          {available.length > displayLimit && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-2 text-xs text-primary-600 hover:text-primary-700"
            >
              Show {available.length - displayLimit} more...
            </button>
          )}
          {showAll && available.length > displayLimit && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="mt-2 text-xs text-accent-500 hover:text-accent-700"
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}

SpecSelector.propTypes = {
  specs: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  availableSpecs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SpecSelector;
